/**
 * Two-step verification settings (#43): authenticator app, passkeys, recovery codes.
 */
import { useState } from "react";
import { Alert, Badge, Button, Form, ListGroup } from "react-bootstrap";
import { QueryState, Section } from "../../components/ui/kit.tsx";
import { ApiError, api } from "../../lib/api.ts";
import { useAuth } from "../../lib/auth.tsx";
import { useFormat } from "../../lib/format.ts";
import { useApi, useInvalidateAll } from "../../lib/query.ts";
import { createPasskey, passkeysSupported } from "../../lib/webauthn.ts";

interface MfaStatus {
  enabled: boolean;
  totp: boolean;
  passkeys: Array<{ id: string; name: string; createdAt: string; lastUsedAt: string | null }>;
  recoveryCodesLeft: number;
}

function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  return (
    <Alert variant="warning">
      <strong>Save these recovery codes now.</strong> Each one lets you sign in once if you lose your phone or
      passkey. They won't be shown again.
      <pre className="my-2 p-2 bg-body rounded font-monospace">{codes.join("\n")}</pre>
      <Button
        size="sm"
        variant="outline-secondary"
        onClick={() => navigator.clipboard?.writeText(codes.join("\n"))}
      >
        Copy
      </Button>{" "}
      <Button size="sm" onClick={onDone}>
        I've saved them
      </Button>
    </Alert>
  );
}

export function SecuritySection() {
  const f = useFormat();
  const { refresh } = useAuth();
  const invalidate = useInvalidateAll();
  const status = useApi<MfaStatus>("/api/account/mfa");
  const [setup, setSetup] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [passkeyName, setPasskeyName] = useState("");

  async function act(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      await invalidate();
      await refresh();
    } catch (e) {
      setError(
        e instanceof ApiError
          ? (Object.values(e.fields)[0] ?? e.message)
          : e instanceof Error
            ? e.message
            : String(e),
      );
    }
  }

  return (
    <Section title="Two-step verification" actions={status.data?.enabled && <Badge bg="success">On</Badge>}>
      <p className="small text-muted">
        After your password, confirm it's you with an authenticator app or a passkey. This protects your tax
        and tenant records even if your password leaks.
      </p>
      {error && <Alert variant="danger">{error}</Alert>}
      {codes && <RecoveryCodes codes={codes} onDone={() => setCodes(null)} />}
      <QueryState query={status}>
        {(s) => (
          <>
            <h3 className="h6 mt-3">Authenticator app</h3>
            {s.totp ? (
              <div className="d-flex align-items-center gap-2 mb-3">
                <span className="small">Set up.</span>
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => act(() => api.delete("/api/account/mfa/totp"))}
                >
                  Remove
                </Button>
              </div>
            ) : setup ? (
              <div className="mb-3">
                <ol className="small ps-3">
                  <li>
                    Open your authenticator app (for example Google Authenticator, Microsoft Authenticator or
                    1Password) and add an account. On your phone you can{" "}
                    <a href={setup.otpauthUri}>open it in the app</a>; otherwise enter this key:
                    <div className="font-monospace fs-6 my-1 user-select-all">
                      {setup.secret.match(/.{1,4}/g)?.join(" ")}
                    </div>
                  </li>
                  <li>Enter the 6-digit code it shows.</li>
                </ol>
                <Form
                  className="d-flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    act(async () => {
                      const res = await api.post<{ recoveryCodes: string[] | null }>(
                        "/api/account/mfa/totp/confirm",
                        { code },
                      );
                      if (res.recoveryCodes) setCodes(res.recoveryCodes);
                      setSetup(null);
                      setCode("");
                    });
                  }}
                >
                  <Form.Control
                    style={{ maxWidth: 160 }}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    aria-label="Code from your app"
                    placeholder="123456"
                  />
                  <Button type="submit" disabled={!code.trim()}>
                    Turn on
                  </Button>
                </Form>
              </div>
            ) : (
              <Button
                size="sm"
                className="mb-3"
                onClick={() => act(async () => setSetup(await api.post("/api/account/mfa/totp", {})))}
              >
                Set up an authenticator app
              </Button>
            )}

            <h3 className="h6">Passkeys</h3>
            {s.passkeys.length > 0 && (
              <ListGroup className="mb-2">
                {s.passkeys.map((p) => (
                  <ListGroup.Item key={p.id} className="d-flex justify-content-between align-items-center">
                    <div>
                      <div>{p.name}</div>
                      <div className="small text-muted">
                        Added {f.date(p.createdAt)}
                        {p.lastUsedAt ? ` · last used ${f.date(p.lastUsedAt)}` : ""}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => act(() => api.delete(`/api/account/mfa/passkeys/${p.id}`))}
                    >
                      Remove
                    </Button>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
            {passkeysSupported() ? (
              <Form
                className="d-flex gap-2 mb-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  act(async () => {
                    const options = await api.post<{
                      challengeId: string;
                      publicKey: Parameters<typeof createPasskey>[0];
                    }>("/api/account/mfa/passkeys/options", {});
                    const credential = await createPasskey(options.publicKey);
                    const res = await api.post<{ recoveryCodes: string[] | null }>(
                      "/api/account/mfa/passkeys",
                      {
                        challengeId: options.challengeId,
                        name: passkeyName,
                        credential,
                      },
                    );
                    if (res.recoveryCodes) setCodes(res.recoveryCodes);
                    setPasskeyName("");
                  });
                }}
              >
                <Form.Control
                  style={{ maxWidth: 240 }}
                  value={passkeyName}
                  onChange={(e) => setPasskeyName(e.target.value)}
                  placeholder="Name, e.g. Laptop"
                  aria-label="Passkey name"
                />
                <Button type="submit" size="sm" variant="outline-primary">
                  Add a passkey
                </Button>
              </Form>
            ) : (
              <p className="small text-muted">This browser doesn't support passkeys.</p>
            )}

            {s.enabled && (
              <>
                <h3 className="h6">Recovery codes</h3>
                <p className="small mb-2">
                  {s.recoveryCodesLeft} of 10 unused.{" "}
                  <Button
                    size="sm"
                    variant="link"
                    className="p-0 align-baseline"
                    onClick={() =>
                      act(async () =>
                        setCodes(
                          (await api.post<{ recoveryCodes: string[] }>("/api/account/mfa/recovery-codes", {}))
                            .recoveryCodes,
                        ),
                      )
                    }
                  >
                    Make new codes
                  </Button>{" "}
                  (the old ones stop working).
                </p>
              </>
            )}
          </>
        )}
      </QueryState>
    </Section>
  );
}
